import { AssetAvailability } from '../../../api/types';
import { BookingStatusType } from '../../../components/StatusBadge';
import { isRoomFamily } from '../../../utils/useCases';

// Status semantics shared by every availability surface: the month chart, the
// single-asset day dialog and the all-objects day overlay. One definition per
// question, so a status can never read one way in the chart and another way in
// the overlay.

// Bar colour per booking status. These are the exact colours the chart has
// always drawn, expressed as design-system palette tokens instead of the
// previous `bg-[#10B981]`-style arbitrary values (raw hex bypasses the design
// system — CLAUDE.md). Purely a notation change: `emerald-500` *is* `#10B981`,
// `blue-500` is `#3B82F6`, and so on down the list.
export const statusColors: Record<BookingStatusType, string> = {
    booked: 'bg-emerald-500',
    active: 'bg-blue-500',
    finished: 'bg-gray-500',
    canceled: 'bg-red-500',
    reserved: 'bg-purple-400',
    incompleted: 'bg-amber-500',
    blocked: 'bg-red-800',
    conflicted: 'bg-pink-800',
    // Shares `incompleted`'s amber: both mean "this slot went unused".
    expired: 'bg-amber-500',
    // A running booking past its end (#2417 F1). Shares `active`'s blue because
    // it IS active — the object is out — but darker, since it is out longer
    // than it was sold for.
    overtime: 'bg-blue-800',
};

// Which status wins when a day cell holds several bookings — worst news first.
export const statusPriority: BookingStatusType[] = [
    // `overtime` outranks `active`: both mean the object is out, and the one
    // that is out past its end is the worse news.
    'conflicted', 'blocked', 'overtime', 'active', 'booked', 'reserved',
    'incompleted', 'expired', 'canceled', 'finished',
];

export const getDominantStatus = (
    statusCounts: Record<BookingStatusType, number>,
): BookingStatusType | null => {
    for (const status of statusPriority) {
        if (statusCounts[status] > 0) return status;
    }
    return null;
};

/**
 * Does a booking in this status actually take the object off the market?
 *
 * Canceled bookings stay visible (the chart draws them and the status filter
 * can select them), but they no longer consume the object — so they must not
 * eat into the free windows the day view reports. Every other status does
 * occupy: `finished` and `incompleted` describe bookings that happened, and
 * `blocked` is a manual block, which is exactly an occupancy.
 */
export const occupiesAsset = (status: BookingStatusType): boolean =>
    status !== 'canceled';

/**
 * How an object is labelled across the availability surfaces.
 *
 * Locker-based objects carry their door number, because that is what staff
 * read off the station; rooms and apartments are named things and carry only
 * their name.
 */
export const assetDisplayLabel = (
    asset: AssetAvailability,
    lockerLabel: string,
): string => {
    const useCase = asset.useCases?.[0];
    const isRoom = isRoomFamily(useCase);
    if (isRoom || !asset.doorNumbers?.length) return asset.assetName;
    return `${lockerLabel} ${asset.doorNumbers[0]} - ${asset.assetName}`;
};
